import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { UserCircle, Shield } from "lucide-react";
import { usePermissions } from "@/providers/permissions-provider";
import { mockUsers, mockUserProfiles } from "@/data/mockUserProfiles";
import { toast } from "sonner";

interface UserSwitcherProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function UserSwitcher({ open: controlledOpen, onOpenChange }: UserSwitcherProps = {}) {
  const { currentUser, setCurrentUser } = usePermissions();
  const [selectedUserId, setSelectedUserId] = useState(currentUser?.id || "");
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const handleSwitch = () => {
    const user = mockUsers.find((u) => u.id === selectedUserId);
    if (user) {
      setCurrentUser(user);
      const profile = mockUserProfiles.find((p) => p.id === user.profileId);
      toast.success(`Switched to ${user.name} (${profile?.name})`);
      setIsOpen(false);
    }
  };

  const getProfileName = (profileId: string) => {
    return mockUserProfiles.find((p) => p.id === profileId)?.name || "Unknown";
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Only show trigger button if not controlled externally */}
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2">
            <UserCircle className="h-4 w-4" />
            <span className="hidden md:inline">Switch User</span>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Switch User Profile</DialogTitle>
          <DialogDescription>
            Select a different user to test various permission levels
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-select">Select User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger id="user-select">
                <SelectValue placeholder="Choose a user" />
              </SelectTrigger>
              <SelectContent>
                {mockUsers
                  .filter((u) => u.isActive)
                  .map((user) => {
                    const profile = mockUserProfiles.find((p) => p.id === user.profileId);
                    return (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <UserCircle className="h-4 w-4" />
                          <span>{user.name}</span>
                          <span className="text-xs text-muted-foreground">({profile?.name})</span>
                        </div>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>

          {selectedUserId && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Profile Details</span>
                <Badge variant="secondary">
                  {mockUserProfiles.find((p) => p.id === mockUsers.find((u) => u.id === selectedUserId)?.profileId)?.permissions.length || 0} permissions
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>{getProfileName(mockUsers.find((u) => u.id === selectedUserId)?.profileId || "")}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {mockUserProfiles.find((p) => p.id === mockUsers.find((u) => u.id === selectedUserId)?.profileId)?.description}
              </p>
            </div>
          )}

          <Button onClick={handleSwitch} className="w-full" disabled={!selectedUserId || selectedUserId === currentUser?.id}>
            Switch User
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
